import styled, { css } from 'styled-components';
import Button from 'v3/components/Button';
import { Text } from 'v3/components/OfferBox/styles';
import { Wrapper as Form } from 'v3/components/SidebarBox/styles';
import { Wrapper as CopyCode } from 'v3/components/CopyCode/styles';

type FieldsWrapperProps = {
  gap?: number;
  marginTop?: number;
};

export const Wrapper = styled.section`
  ${({ theme }) => css`
    @media (max-width: 992px) {
      ${Form} {
        /* Halved rather than zeroed, matching ReviewOffer. With the General wrapper
           no longer padding at this breakpoint, zero here would leave the content
           flush against the viewport edges. */
        padding: 16px 8px;
        border: none;
        background-color: ${theme.colors.neutralSilver5};
      }
    }
  `}
`;

export const TitleSection = styled.section`
  margin-bottom: 40px;
`;

export const Title = styled.h2`
  ${({ theme }) => css`
    font-size: 3.2rem;
    line-height: 3.8rem;
    font-weight: 600;
    color: ${theme.colors.neutralSilver80};
    margin-bottom: 40px;

    @media (max-width: 992px) {
      font-size: 2.4rem;
      line-height: 1.23;
    }
  `}
`;

export const Description = styled.p`
  ${({ theme }) => css`
    font-size: 1.6rem;
    line-height: 2.6rem;
    font-weight: 400;
    color: ${theme.colors.neutralSilver90};
    margin-bottom: 24px;
  `}
`;

export const Learn = styled.div`
  margin: 40px 0;
`;

export const CollapseContainer = styled.div`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.neutralSilver10};
    margin-top: 20px;
    padding: 20px 0 4px;

    .ant-collapse .ant-collapse-item .ant-collapse-header {
      padding: 0 10px;
    }

    .ant-collapse-content > .ant-collapse-content-box {
      padding: 0;
    }
  `}
`;

export const ButtonSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;

  > button {
    min-width: 272px;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;

    > button {
      display: flex;
      justify-content: center;
      width: 100%;
    }
  }
`;

export const OfferBox = styled.div`
  ${Text} {
    @media (min-width: 768px) {
      font-size: 1.6rem;
      line-height: 2.6rem;
    }
  }
`;

export const FieldsWrapper = styled.div<FieldsWrapperProps>`
  ${({ gap = 16, marginTop = 0 }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${gap}px;
    margin-top: ${marginTop}px;
    width: 100%;

    > div {
      flex: 1;
    }

    label {
      font-size: 1.6rem;
    }

    @media (max-width: 450px) {
      flex-wrap: wrap;

      > div {
        flex: auto;
        width: 100%;
      }
    }
  `}
`;

export const TermsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;

  @media (max-width: 400px) {
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-start;
  }
`;

export const RadioLabel = styled.p`
  ${({ theme }) => css`
    font-weight: 500;
    font-size: 1.6rem;
    line-height: 2.6rem;
    color: ${theme.colors.neutralSilver70};

    margin: 20px 0;
  `}
`;

export const RecalculateButton = styled(Button)`
  width: 100%;
  justify-content: center;
  max-width: 264px;
  margin-top: 28px;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export const SubscribeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 42px;

  > button {
    min-width: 170px;
    display: flex;
    justify-content: center;
  }
`;

export const SubscribeIco = styled.img`
  margin-bottom: 16px;
`;

export const SubscribeTitle = styled.h3`
  ${({ theme }) => css`
    font-size: 2.4rem;
    font-weight: 600;
    color: ${theme.colors.neutralSilver80};
    margin-bottom: 8px;
  `}
`;

export const BrandedText = styled.span`
  ${({ theme }) => css`
    background-color: ${theme.colors.brandSecondary80};
    background: linear-gradient(270deg, ${theme.colors.brandSecondary80} 0%, ${theme.colors.brandPrimary70} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  `}
`;

export const SubscribeDescription = styled.p`
  ${({ theme }) => css`
    font-size: 1.8rem;
    font-weight: 600;
    color: ${theme.colors.neutralSilver70};
    margin-bottom: 16px;

    > span {
      color: ${theme.colors.brandSecondary70};
    }
  `}
`;

export const SubscribeText = styled.p`
  ${({ theme }) => css`
    font-size: 1.4rem;
    font-weight: 600;
    color: ${theme.colors.neutralSilver60};
    margin: 16px 0 12px 0;
  `}
`;

export const InviteSection = styled.div`
  margin: 0 0 0 0;
`;

export const InviteDescription = styled.p`
  ${({ theme }) => css`
    font-size: 1.6rem;
    font-weight: 500;
    color: ${theme.colors.neutralSilver60};
    margin-bottom: 24px;
  `}
`;

export const Hr = styled.hr`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.neutralSilver10};
    margin: 24px 0;
  `}
`;

export const EmailsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;

  > button {
    margin-top: 8px;
    min-width: 170px;
    display: flex;
    justify-content: center;
  }
`;

export const EmailsLabel = styled.div`
  width: 100%;
`;

export const EmailTitle = styled.h4`
  ${({ theme }) => css`
    font-size: 1.4rem;
    font-weight: 500;
    color: ${theme.colors.neutralSilver70};
    line-height: 22px;
    margin: 0;
  `}
`;

export const EmailDescription = styled.p`
  ${({ theme }) => css`
    font-size: 1.2rem;
    color: ${theme.colors.neutralSilver50};
    margin-bottom: 8px;
  `}
`;

export const ReferralSection = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.neutralSilver5};
    padding: 24px;
    width: 100%;
    border-radius: 8px;
    display: flex;
    gap: 16px;
    justify-content: space-between;

    ${CopyCode} {
      width: 280px;
    }

    @media (max-width: 576px) {
      flex-direction: column;

      ${CopyCode} {
        width: 100%;
      }
    }
  `}
`;

export const ReferralContent = styled.div``;

export const ReferralText = styled.p`
  ${({ theme }) => css`
    font-size: 1.4rem;
    color: ${theme.colors.neutralSilver60};
    line-height: 1.6;
    margin: 0;

    > strong {
      font-weight: 700;
      color: ${theme.colors.brandPrimary70};
    }
  `}
`;

export const SubscribeContent = styled.div<{ noTopPadding?: boolean }>`
  ${({ theme, noTopPadding = false }) => css`
    z-index: 2;
    position: relative;
    background-color: ${theme.colors.globalWhite100};
    padding: ${noTopPadding ? '0px 0 0 0' : '42px 0 0 0'};

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: -24px;
      width: 24px;
      height: 100%;
      z-index: 1;
      background-color: ${theme.colors.globalWhite100};
    }

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      right: -24px;
      width: 24px;
      height: 100%;
      z-index: 1;
      background-color: ${theme.colors.globalWhite100};
    }

    @media (max-width: 992px) {
      padding: 24px 0 48px 0;
      margin-top: 32px;
      margin-bottom: -80px;
    }
  `}
`;

export const LoadingWrapper = styled.div`
  padding: 72px 0;
`;

export const LoadingContent = styled.p`
  text-align: center;
`;
